import React from "react";
import { useTranslation } from "react-i18next";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";

import { CustomErrorBoundary } from "@components/CustomErrorBoundary";

import { LanguageCardWrapper } from "./LanguageCardWrapper";
import { getNestedValue, Language, languages } from "@constants/template";
import { templateActions } from "../../templateStore";
import classNames from "classnames";
import { ScrollContainer } from "@components/ScrollContainer";
import { useSelector } from "react-redux";

type LanguagesProps = {
    type: string;
};

export function Languages({ type }: LanguagesProps): JSX.Element {
    const { t } = useTranslation();
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );
    const [activeTab, setActiveTab] = React.useState<number>(0);
    function toggleNav(tab: number) {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    }
    const backendData = getNestedValue([type, "Divers", "Langue"]);

    const [languagesItems, setLanguagesItems] = React.useState<Language[]>(() =>
        languages.map((lang) => {
            const isDefault = lang.key === backendData.Faivorit;
            return {
                ...lang,
                active: (isDefault || backendData[lang.key]) ?? false,
                isDefault,
            };
        })
    );

    const handleLanguageToggle = (id: string) => {
        setLanguagesItems((prev) =>
            prev.map((item) => {
                if (item.id === id) {
                    if (item.isDefault) return item;
                    const updatedActive = !item.active;
                    templateActions.updateTemplate(
                        ["template", type, "Divers", "Langue", item.key],
                        updatedActive
                    );
                    return { ...item, active: updatedActive };
                }
                return item;
            })
        );
    };

    const handleSetDefault = (id: string) => {
        setLanguagesItems((prev) => {
            let defaultLanguageKey = "";

            const newItems = prev.map((item) => {
                const isDefault = item.id === id;
                if (isDefault) {
                    defaultLanguageKey = item.key;
                }

                return {
                    ...item,
                    isDefault,
                    active: isDefault ? true : item.active,
                };
            });

            if (defaultLanguageKey) {
                templateActions.updateTemplate(
                    ["template", type, "Divers", "Langue", "Faivorit"],
                    defaultLanguageKey
                );
                templateActions.updateTemplate(
                    ["template", type, "Divers", "Langue", defaultLanguageKey],
                    true
                );
            }

            return newItems;
        });
    };
    function chunkArray(array: any, size: any) {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    }
    const firstRowCount =
        languagesItems.length > 4 && languagesItems.length <= 6 ? 3 : 4;

    const rows = chunkArray(languagesItems, firstRowCount);

    const tabs = [
        {
            title: t("Languages choice"),
            content: (
                <CustomErrorBoundary>
                    {languagesItems.length > 8 ? (
                        <ScrollContainer>
                            <div
                                className="d-flex flex-column justify-content-center align-items-center"
                                style={{ gap: "25px", minHeight: "50vh" }}
                            >
                                {rows.map((row: any, rowIndex: any) => (
                                    <div
                                        key={rowIndex}
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "center",
                                            gap: "50px",
                                        }}
                                    >
                                        {row.map((item: any) => (
                                            <LanguageCardWrapper
                                                key={item.id}
                                                item={item}
                                                onToggle={handleLanguageToggle}
                                                onSetDefault={handleSetDefault}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </ScrollContainer>
                    ) : (
                        <div
                            className="d-flex flex-column justify-content-center align-items-center"
                            style={{ gap: "25px", minHeight: "50vh" }}
                        >
                            {rows.map((row: any, rowIndex: any) => (
                                <div
                                    key={rowIndex}
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        gap: "50px",
                                    }}
                                >
                                    {row.map((item: any) => (
                                        <LanguageCardWrapper
                                            key={item.id}
                                            item={item}
                                            onToggle={handleLanguageToggle}
                                            onSetDefault={handleSetDefault}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </CustomErrorBoundary>
            ),
            customClassName: "",
        },
    ];

    return (
        <React.Fragment>
            <div style={{ borderBottom: "1px solid #ddd4d4" }}>
                <Nav
                    tabs
                    style={{
                        backgroundColor: "white",
                        gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
                        border: "unset",
                    }}
                >
                    {tabs.map((element, index) => (
                        <NavItem
                            key={index}
                            className={classNames(
                                "pb-1",
                                "d-flex align-items-center text-nowrap",
                                {
                                    "disable-nav-item__clz":
                                        activeTab !== index,
                                },
                                element.customClassName
                            )}
                            style={{
                                cursor: "pointer",
                                borderBottom:
                                    activeTab === index
                                        ? `${mainColor} 3px solid`
                                        : "",
                            }}
                        >
                            <NavLink
                                className={classNames("text-dark w-100", {
                                    "active customer-account-menu-nav-link-active__clz":
                                        activeTab === index,
                                    "customer-account-menu-nav-link-disable__clz":
                                        activeTab !== index,
                                })}
                                onClick={() => toggleNav(index)}
                            >
                                <span
                                    className="px-2"
                                    style={{
                                        font:
                                            "normal normal 600 20px/27px Segoe UI",
                                        marginRight: index === 0 ? "20px" : "0",
                                    }}
                                >
                                    {element.title}
                                </span>
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
            </div>
            <TabContent activeTab={activeTab} className="h-100">
                {tabs.map((element, index) => (
                    <TabPane
                        tabId={index}
                        key={index}
                        style={{
                            height: "100%",
                            marginTop: "4rem",
                        }}
                    >
                        {element.content}
                    </TabPane>
                ))}
            </TabContent>
        </React.Fragment>
    );
}
